/**
 * Чисті функції форматування для FieldDisplay. Винесені, щоб можна було
 * викликати і в snippet-renderах (PersonDetail rows), і у table-cells.
 */

const FALLBACK = '—';

export function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || v === '';
}

export function formatText(v: unknown): string {
  if (isEmpty(v)) return FALLBACK;
  return String(v);
}

/** Currency stored as integer cents → ₴ або інша currency code від field.computation? */
export function formatCurrencyCents(v: unknown, currency = 'UAH'): string {
  if (isEmpty(v)) return FALLBACK;
  const n = Number(v);
  if (!Number.isFinite(n)) return FALLBACK;
  const major = n / 100;
  try {
    return new Intl.NumberFormat('uk-UA', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(major);
  } catch {
    return `${major.toFixed(2)} ${currency}`;
  }
}

export function formatNumber(v: unknown): string {
  if (isEmpty(v)) return FALLBACK;
  const n = Number(v);
  if (!Number.isFinite(n)) return FALLBACK;
  return new Intl.NumberFormat('uk-UA').format(n);
}

export function formatPercent(v: unknown): string {
  if (isEmpty(v)) return FALLBACK;
  const n = Number(v);
  if (!Number.isFinite(n)) return FALLBACK;
  return `${n}%`;
}

export function formatDate(v: unknown): string {
  if (isEmpty(v)) return FALLBACK;
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return FALLBACK;
  return d.toLocaleDateString('uk-UA');
}

export function formatDateTime(v: unknown): string {
  if (isEmpty(v)) return FALLBACK;
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return FALLBACK;
  return d.toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' });
}

export function formatCheckbox(v: unknown): string {
  return v ? '✓' : '—';
}

/** Перетворити `2026-06-01T10:30:00Z` → `2026-06-01T10:30` для datetime-local input. */
export function toDateTimeLocalValue(v: unknown): string {
  if (isEmpty(v)) return '';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function toDateValue(v: unknown): string {
  if (isEmpty(v)) return '';
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Маска email для READER: `j***@example.com`. Idempotent: якщо вже масковано — повертає як є. */
export function maskEmail(s: string): string {
  const at = s.indexOf('@');
  if (at < 1) return s;
  const local = s.slice(0, at);
  if (local.includes('***')) return s; // вже масковано
  const head = local.charAt(0);
  return `${head}***${s.slice(at)}`;
}

/** Маска phone для READER: зберігаємо префікс (+380) і останні 2 цифри, решта зірочки. */
export function maskPhone(s: string): string {
  const digits = s.replace(/[^0-9+]/g, '');
  if (digits.length < 5) return s;
  if (s.includes('***')) return s;
  const prefix = digits.startsWith('+') ? digits.slice(0, 4) : digits.slice(0, 3);
  const tail = digits.slice(-2);
  return `${prefix}***${tail}`;
}

/** Tags як масив рядків АБО comma-separated string. Повертаємо array нормалізовано. */
export function parseTags(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
  if (typeof v === 'string') {
    return v
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}
